import { useEffect, useRef, useState } from "react";
import { ArrowDown } from "@phosphor-icons/react";
import { haptic } from "../lib/haptics";

/** Native-feel pull-to-refresh on touch devices. Wraps a section; calls
 * `onRefresh` when the user drags down by `threshold` (default 80px) at
 * the top of the page and releases. Disabled on desktop & on non-touch.
 *
 * Usage:
 *   <PullToRefresh onRefresh={fetchData}>
 *     <YourList />
 *   </PullToRefresh>
 */
export default function PullToRefresh({ onRefresh, children, threshold = 80, className = "" }) {
  const containerRef = useRef(null);
  const startYRef = useRef(null);
  const draggingRef = useRef(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const triggeredHapticRef = useRef(false);

  useEffect(() => {
    // Only enable on touch devices with mobile width
    if (typeof window === "undefined") return;
    if (!("ontouchstart" in window) && !navigator.maxTouchPoints) return;

    const el = containerRef.current;
    if (!el) return;

    const onTouchStart = (e) => {
      if (refreshing) return;
      // Only trigger if scrolled to the top
      const scrollEl = el.closest("main") || document.scrollingElement || document.documentElement;
      if ((scrollEl?.scrollTop || 0) > 1) { startYRef.current = null; return; }
      const t = e.touches?.[0];
      if (!t) return;
      startYRef.current = t.clientY;
      draggingRef.current = true;
      triggeredHapticRef.current = false;
    };
    const onTouchMove = (e) => {
      if (!draggingRef.current || refreshing) return;
      const t = e.touches?.[0];
      if (!t || startYRef.current == null) return;
      const dy = t.clientY - startYRef.current;
      if (dy < 0) { setPullDistance(0); return; }
      // Apply rubber-band easing
      const eased = Math.min(160, dy * 0.5);
      setPullDistance(eased);
      if (eased >= threshold && !triggeredHapticRef.current) {
        triggeredHapticRef.current = true;
        haptic("light");
      } else if (eased < threshold && triggeredHapticRef.current) {
        triggeredHapticRef.current = false;
      }
    };
    const onTouchEnd = async () => {
      if (!draggingRef.current) return;
      draggingRef.current = false;
      const shouldFire = pullDistance >= threshold;
      setPullDistance(0);
      startYRef.current = null;
      if (shouldFire && onRefresh) {
        setRefreshing(true);
        haptic("medium");
        try { await Promise.resolve(onRefresh()); }
        catch { /* ignore */ }
        finally { setRefreshing(false); }
      }
    };

    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: true });
    el.addEventListener("touchend", onTouchEnd, { passive: true });
    el.addEventListener("touchcancel", onTouchEnd, { passive: true });
    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
      el.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [onRefresh, threshold, refreshing, pullDistance]);

  const indicator = refreshing ? "refreshing" : pullDistance >= threshold ? "release" : "pull";

  return (
    <div ref={containerRef} className={className} data-testid="pull-to-refresh">
      <div
        style={{
          height: refreshing ? 56 : `${pullDistance}px`,
          opacity: refreshing ? 1 : Math.min(1, pullDistance / threshold),
          transition: refreshing || pullDistance === 0 ? "height 200ms ease-out, opacity 200ms ease-out" : "none",
        }}
        className="overflow-hidden"
        aria-hidden="true"
      >
        <div className="pwa-pull-spinner">
          {refreshing ? (
            <>
              <span className="pwa-pull-spinner-icon mr-2" />
              <span>Refreshing…</span>
            </>
          ) : (
            <>
              <ArrowDown size={16} className={`pwa-pull-arrow mr-2 ${indicator === "release" ? "pulling" : ""}`} />
              <span>{indicator === "release" ? "Release to refresh" : "Pull down to refresh"}</span>
            </>
          )}
        </div>
      </div>
      {children}
    </div>
  );
}
